import React from 'react'
import T from 'prop-types'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import FavoriteIcon from '@mui/icons-material/Favorite'

import ChoiceRecap from 'components/atoms/ChoiceRecap'

import {
  FILTER_CAPS_FIRST_CHOICE,
  FILTER_CAPS_NONE_CHOICE,
  FILTER_CAPS_ALL_CHOICES,
  FILTER_CAPS_NO_CHOICES,
  FILTER_CAPS_BLIND_OFFERS,
} from 'hooks/useCarsFilters'

const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'

export default function ChoicesRecap({
  variant = VARIANT_LIST,
  cars = [],
  caps,
  blindOffersUsed,
  blindOffersTotal,
  showBlindOffers = false,
  filteringCap,
  setFilteringCaps,
  onlyFavorites = false,
  onToggleFavorites,
  readOnly = false,
}) {
  const { t } = useTranslation()

  const numberFormatter = new Intl.NumberFormat('it-IT')

  const isDetails = variant === VARIANT_DETAILS

  const hasBlindPrice = (car) =>
    car?.blindPrice !== null &&
    car?.blindPrice !== undefined &&
    car?.blindPrice !== ''

  const hasFirstChoice = (car) => !!car?.firstChoice

  const hasSecondChoice = (car) => !!car?.secondChoice

  const hasChoice = (car) =>
    hasFirstChoice(car) || hasSecondChoice(car) || hasBlindPrice(car)

  const recapCaps = {
    max: { ...caps, total: cars.length || 0 },
    selected: {
      allChoices: cars.filter((car) => hasChoice(car)).length,
      noChoices: cars.filter((car) => !hasChoice(car)).length,
      firstChoice: cars.filter((car) => hasFirstChoice(car)).length,
      favorites: cars.filter((car) => car.favorite).length,
      total: cars.reduce(
        (previousTotal, car) =>
          ((hasChoice(car) && car.price) || 0) + previousTotal,
        0
      ),
    },
  }

  return (
    <>
      {isDetails && (
        <Typography
          variant="body2"
          color="primaryText"
          sx={{ pb: 1 }}
          data-testid="choices-recap-details-title"
        >
          <strong>{t('carsToBuy.labelTitle.yourSelection')}</strong>
        </Typography>
      )}
      <Stack direction="row" data-testid="choices-recap">
        {!isDetails && (
          <>
            <ChoiceRecap
              id="total"
              title={t('carsToBuy.labelTitle.allCars')}
              value={recapCaps.max.total}
              handleClick={() => setFilteringCaps(FILTER_CAPS_NONE_CHOICE)}
              border
            />
            <Box pr={2} />
            <Divider orientation="vertical" variant="middle" flexItem />
            <Box pr={2} />
          </>
        )}
        <ChoiceRecap
          id={FILTER_CAPS_FIRST_CHOICE}
          title={t('carsToBuy.labelTitle.firstChoice')}
          value={`${recapCaps.selected.firstChoice}/${recapCaps.max.firstChoice}`}
          handleClick={
            !isDetails
              ? () => setFilteringCaps(FILTER_CAPS_FIRST_CHOICE)
              : undefined
          }
          selected={filteringCap === FILTER_CAPS_FIRST_CHOICE}
          border
          sx={{ flex: isDetails ? 1 : undefined }}
        />
        <Box pr={1} />
        {!isDetails && showBlindOffers && (
          <>
            <ChoiceRecap
              id={FILTER_CAPS_BLIND_OFFERS}
              title={t('carsToBuy.labelTitle.blindOffers')}
              value={`${blindOffersUsed}/${blindOffersTotal}`}
              handleClick={() => setFilteringCaps(FILTER_CAPS_BLIND_OFFERS)}
              selected={filteringCap === FILTER_CAPS_BLIND_OFFERS}
              border
            />
            <Box pr={1} />
          </>
        )}
        {!isDetails && (
          <>
            <ChoiceRecap
              id={FILTER_CAPS_ALL_CHOICES}
              title={t('carsToBuy.labelTitle.allChoices')}
              value={`${recapCaps.selected.allChoices}`}
              handleClick={() => setFilteringCaps(FILTER_CAPS_ALL_CHOICES)}
              selected={filteringCap === FILTER_CAPS_ALL_CHOICES}
              border
            />
            <Box pr={1} />
            <ChoiceRecap
              id={FILTER_CAPS_NO_CHOICES}
              title={t('carsToBuy.labelTitle.noChoices')}
              value={`${recapCaps.selected.noChoices}`}
              handleClick={() => setFilteringCaps(FILTER_CAPS_NO_CHOICES)}
              selected={filteringCap === FILTER_CAPS_NO_CHOICES}
              border
            />
            {onToggleFavorites && (
              <>
                <Box pr={1} />
                <ChoiceRecap
                  id="favorites"
                  title={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <FavoriteIcon
                        sx={{
                          fontSize: 14,
                          color: onlyFavorites ? 'error.main' : 'inherit',
                        }}
                      />
                      <span>{t('carsToBuy.labelTitle.favorites')}</span>
                    </Stack>
                  }
                  value={`${recapCaps.selected.favorites}`}
                  handleClick={onToggleFavorites}
                  selected={onlyFavorites}
                  border
                />
              </>
            )}
            <Box pr={2} />
            <Divider orientation="vertical" variant="middle" flexItem />
            <ChoiceRecap
              title={t('carsToBuy.labelTitle.totalValueSelected')}
              value={`€ ${numberFormatter.format(recapCaps.selected.total)}`}
            />
          </>
        )}
      </Stack>
    </>
  )
}

ChoicesRecap.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  cars: T.array,
  caps: T.object,
  blindOffersUsed: T.number,
  blindOffersTotal: T.number,
  showBlindOffers: T.bool,
  filteringCap: T.string,
  setFilteringCaps: T.func,
  onlyFavorites: T.bool,
  onToggleFavorites: T.func,
  readOnly: T.bool,
}
